import HttpClient from "../../utils/functions/HttpClient";
import ServerEndpoints from "../../utils/constants/ServerEndpoints";

const getTotalElements = (data) =>
  data?.page?.totalElements ?? data?.totalElements ?? 0;

const normalizeQueryParams = (params = {}) => {
  const normalizedParams = { ...params };

  if (normalizedParams.pageSize != null && normalizedParams.size == null) {
    normalizedParams.size = normalizedParams.pageSize;
  }

  delete normalizedParams.pageSize;
  return normalizedParams;
};

const RESERVATION_ACTIVE_STATUSES = new Set(["Booked", "Pending", "Requested", "Confirmed"]);
const RESERVATION_COMPLETED_STATUSES = new Set(["Done", "Completed"]);
const RESERVATION_INACTIVE_STATUSES = new Set(["Cancelled"]);
const APPOINTMENT_INACTIVE_STATUSES = new Set(["Cancelled", "No Show"]);
const APPOINTMENT_COMPLETED_STATUSES = new Set(["Done", "Completed"]);
const INVOICE_OUTSTANDING_STATUSES = new Set(["Due", "Overdue", "Due Immediately", "Partially Paid"]);

const fetchPageContent = async (endpoint, params) => {
  const response = await HttpClient.getAxiosClient().get(endpoint, {
    params: normalizeQueryParams(params),
  });

  return response.data;
};

export const getReservationsSummary = async () => {
  try {
    const data = await fetchPageContent(ServerEndpoints.ADMIN_RESERVATIONS_ENDPOINT, {
      pageSize: 1000,
      page: 0,
    });

    const allReservations = data?.content ?? [];
    const booked = allReservations.filter((r) => RESERVATION_ACTIVE_STATUSES.has(r.status)).length;
    const pending = allReservations.filter((r) => ["Pending", "Requested"].includes(r.status)).length;
    const cancelled = allReservations.filter((r) => RESERVATION_INACTIVE_STATUSES.has(r.status)).length;
    const done = allReservations.filter((r) => RESERVATION_COMPLETED_STATUSES.has(r.status)).length;
    const total = allReservations.filter((r) => !RESERVATION_INACTIVE_STATUSES.has(r.status)).length;

    return { booked, pending, cancelled, done, total, all: allReservations };
  } catch {
    return { booked: 0, pending: 0, cancelled: 0, done: 0, total: 0, all: [] };
  }
};

export const getAppointmentsSummary = async () => {
  try {
    const data = await fetchPageContent(ServerEndpoints.ADMIN_APPOINTMENTS_ENDPOINT, {
      pageSize: 1000,
      page: 0,
    });

    const appointments = data?.content ?? [];
    const now = new Date();
    const activeAppointments = appointments.filter(
      (a) => !APPOINTMENT_INACTIVE_STATUSES.has(a.status)
    );
    const upcoming = activeAppointments.filter(
      (a) => new Date(a.dateTime) > now && !APPOINTMENT_COMPLETED_STATUSES.has(a.status)
    );

    return { total: activeAppointments.length, upcoming: upcoming.length, all: appointments };
  } catch {
    return { total: 0, upcoming: 0, all: [] };
  }
};

export const getInvoicesSummary = async () => {
  try {
    const data = await fetchPageContent(ServerEndpoints.INVOICES_ENDPOINT, {
      pageSize: 1000,
      page: 0,
    });

    const invoices = data?.content ?? [];
    const statusCounts = {};
    let totalRevenue = 0;
    let totalOutstanding = 0;

    invoices.forEach((inv) => {
      const status = inv.status || "Unknown";
      statusCounts[status] = (statusCounts[status] || 0) + 1;
      totalRevenue += Number(inv.totalAmountPaid || 0);

      if (INVOICE_OUTSTANDING_STATUSES.has(status)) {
        totalOutstanding += Number(inv.amountDue || 0);
      }
    });

    return {
      total: invoices.length,
      invoices,
      statusCounts,
      totalRevenue,
      totalOutstanding,
    };
  } catch {
    return { total: 0, invoices: [], statusCounts: {}, totalRevenue: 0, totalOutstanding: 0 };
  }
};

export const getUsersSummary = async () => {
  try {
    const response = await HttpClient.getAxiosClient().get(
      `${ServerEndpoints.ADMIN_USERS_ENDPOINT}`,
      { params: { size: 1, page: 0 } }
    );
    return { total: getTotalElements(response.data) };
  } catch {
    return { total: 0 };
  }
};

export const buildMonthlyRevenueData = (invoices) => {
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const now = new Date();
  const data = [];

  for (let i = 5; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const monthInvoices = invoices.filter((inv) => {
      const issued = new Date(inv.issuedDate);
      return issued.getMonth() === d.getMonth() && issued.getFullYear() === d.getFullYear();
    });
    const revenue = monthInvoices.reduce((sum, inv) => sum + Number(inv.totalAmountPaid || 0), 0);
    data.push({ month: months[d.getMonth()], revenue: Math.round(revenue * 100) / 100 });
  }
  return data;
};

export const buildInvoiceStatusData = (statusCounts) => {
  const colorMap = {
    Paid: "#10b981",
    Due: "#f59e0b",
    Overdue: "#ef4444",
    "Due Immediately": "#f97316",
    "Partially Paid": "#8b5cf6",
    Withdrawn: "#6b7280",
    Pending: "#3b82f6",
  };

  return Object.entries(statusCounts).map(([status, count]) => ({
    id: status,
    label: status,
    value: count,
    color: colorMap[status] || "#94a3b8",
  }));
};

export const buildEventTypeData = (reservations) => {
  const counts = {};
  reservations
    .filter((r) => !RESERVATION_INACTIVE_STATUSES.has(r.status))
    .forEach((r) => {
      const type = r.eventType || "Other";
      counts[type] = (counts[type] || 0) + 1;
    });

  return Object.entries(counts)
    .map(([type, count]) => ({ eventType: type, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 8);
};

export const buildUpcomingReservations = (reservations) => {
  const now = new Date();
  const weekFromNow = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);

  return reservations
    .filter((r) => {
      const start = new Date(r.startingDateTime);
      return (
        start >= now &&
        start <= weekFromNow &&
        RESERVATION_ACTIVE_STATUSES.has(r.status)
      );
    })
    .sort((a, b) => new Date(a.startingDateTime) - new Date(b.startingDateTime))
    .slice(0, 6);
};

export const buildRecentActivity = (reservations, invoices, appointments) => {
  const activities = [];

  reservations
    .filter((r) => !RESERVATION_INACTIVE_STATUSES.has(r.status))
    .slice()
    .sort((a, b) => new Date(b.startingDateTime) - new Date(a.startingDateTime))
    .slice(0, 5)
    .forEach((r) => {
      activities.push({
        id: `res-${r.id}`,
        type: "reservation",
        title: `Reservation #${r.id}`,
        subtitle: `${r.eventType} · ${r.numberOfSeats} guests`,
        status: r.status,
        date: r.startingDateTime,
        amount: r.totalPrice,
      });
    });

  invoices
    .filter((inv) => inv.status !== "Withdrawn")
    .slice()
    .sort((a, b) => new Date(b.issuedDate) - new Date(a.issuedDate))
    .slice(0, 5)
    .forEach((inv) => {
      activities.push({
        id: `inv-${inv.id}`,
        type: "invoice",
        title: `Invoice ${inv.invoiceNumber}`,
        subtitle: `Status: ${inv.status}`,
        status: inv.status,
        date: inv.issuedDate,
        amount: inv.amountDue,
      });
    });

  appointments
    .filter((appt) => !APPOINTMENT_INACTIVE_STATUSES.has(appt.status))
    .slice()
    .sort((a, b) => new Date(b.dateTime) - new Date(a.dateTime))
    .slice(0, 3)
    .forEach((appt) => {
      activities.push({
        id: `appt-${appt.id}`,
        type: "appointment",
        title: `Appointment with ${appt.firstName} ${appt.lastName}`,
        subtitle: appt.raison,
        status: appt.status || "Scheduled",
        date: appt.dateTime,
      });
    });

  return [...activities]
    .sort((a, b) => new Date(b.date) - new Date(a.date))
    .slice(0, 8);
};

/**
 * Fetch reviews summary — average rating, distribution, total, recent.
 */
export const getReviewsSummary = async () => {
  try {
    const response = await HttpClient.getAxiosClient().get(
      `${ServerEndpoints.REVIEWS_ENDPOINT}`,
      { params: { page: 0, size: 1000 } }
    );
    const reviews = response.data?.content ?? [];
    const total = getTotalElements(response.data) || reviews.length;

    if (total === 0) return { total: 0, average: 0, distribution: {}, recent: [] };

    const sum = reviews.reduce((s, r) => s + (r.rating || 0), 0);
    const average = Math.round((sum / reviews.length) * 10) / 10;

    const distribution = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
    reviews.forEach((r) => {
      const star = Math.min(5, Math.max(1, Math.round(r.rating || 0)));
      distribution[star] = (distribution[star] || 0) + 1;
    });

    const recent = [...reviews]
      .sort((a, b) => new Date(b.postedDate) - new Date(a.postedDate))
      .slice(0, 5);

    return { total, average, distribution, recent };
  } catch {
    return { total: 0, average: 0, distribution: {}, recent: [] };
  }
};

/**
 * Fetch receipts to analyze payment method distribution.
 */
export const getReceiptsSummary = async () => {
  try {
    const response = await HttpClient.getAxiosClient().get(
      `${ServerEndpoints.RECEIPT_ENDPOINT}`,
      { params: { pageSize: 1000, page: 0 } }
    );
    const receipts = response.data?.content ?? [];
    return { total: getTotalElements(response.data) || receipts.length, receipts };
  } catch {
    return { total: 0, receipts: [] };
  }
};

/**
 * Fetch contact inquiries summary.
 */
export const getContactInquiriesSummary = async () => {
  try {
    const response = await HttpClient.getAxiosClient().get(
      `${ServerEndpoints.ADMIN_CONTACT_ENDPOINT}`,
      { params: { page: 0, size: 1000 } }
    );
    const inquiries = response.data?.content ?? [];
    const total = getTotalElements(response.data) || inquiries.length;

    const statusCounts = { NEW: 0, READ: 0, RESPONDED: 0, CLOSED: 0 };
    inquiries.forEach((inq) => {
      const st = inq.status || "NEW";
      statusCounts[st] = (statusCounts[st] || 0) + 1;
    });

    return { total, unread: statusCounts.NEW, statusCounts, inquiries };
  } catch {
    return { total: 0, unread: 0, statusCounts: {}, inquiries: [] };
  }
};

/**
 * Build payment method distribution from receipts.
 */
export const buildPaymentMethodData = (receipts) => {
  const counts = {};
  receipts.forEach((r) => {
    const payments = r.payments || [];
    payments.forEach((p) => {
      const method = p.method || "Other";
      counts[method] = (counts[method] || 0) + 1;
    });
  });

  const colorMap = {
    Card: "#3b82f6",
    Cash: "#10b981",
    CashApp: "#8b5cf6",
    Check: "#f59e0b",
    Zelle: "#6366f1",
    Other: "#6b7280",
  };

  return Object.entries(counts).map(([method, count]) => ({
    id: method,
    label: method,
    value: count,
    color: colorMap[method] || "#94a3b8",
  }));
};

/**
 * Build overdue invoice aging buckets.
 */
export const buildOverdueAgingData = (invoices) => {
  const now = new Date();
  const buckets = { "0-30 days": 0, "30-60 days": 0, "60-90 days": 0, "90+ days": 0 };
  let overdueTotal = 0;

  invoices
    .filter((inv) => ["Due", "Overdue", "Due Immediately"].includes(inv.status))
    .forEach((inv) => {
      const due = new Date(inv.dueDate);
      const daysOverdue = Math.floor((now - due) / (1000 * 60 * 60 * 24));
      if (daysOverdue < 0) {
        return;
      }

      overdueTotal += Number(inv.amountDue || 0);
      if (daysOverdue <= 30) buckets["0-30 days"]++;
      else if (daysOverdue <= 60) buckets["30-60 days"]++;
      else if (daysOverdue <= 90) buckets["60-90 days"]++;
      else buckets["90+ days"]++;
    });

  const colorMap = {
    "0-30 days": "#f59e0b",
    "30-60 days": "#f97316",
    "60-90 days": "#ef4444",
    "90+ days": "#dc2626",
  };

  return {
    overdueTotal,
    bars: Object.entries(buckets)
      .filter(([, v]) => v > 0)
      .map(([label, count]) => ({ bucket: label, count, color: colorMap[label] })),
  };
};

/**
 * Extract VIP stats from reservations.
 */
export const buildVipStats = (reservations) => {
  const activeReservations = reservations.filter(
    (r) => !RESERVATION_INACTIVE_STATUSES.has(r.status)
  );
  const vip = activeReservations.filter((r) => r.priorityLevel === "VIP");
  const rescheduled = activeReservations.filter((r) => (r.rescheduleCount || 0) > 0);
  return {
    vipCount: vip.length,
    vipActive: vip.filter((r) => RESERVATION_ACTIVE_STATUSES.has(r.status)).length,
    rescheduleCount: rescheduled.length,
    rescheduleRate: activeReservations.length > 0
      ? Math.round((rescheduled.length / activeReservations.length) * 100)
      : 0,
  };
};

/**
 * Build appointment type breakdown.
 */
export const buildAppointmentTypeData = (appointments) => {
  const counts = {};
  appointments
    .filter((a) => !APPOINTMENT_INACTIVE_STATUSES.has(a.status))
    .forEach((a) => {
      const type = a.appointmentType || "Other";
      counts[type] = (counts[type] || 0) + 1;
    });
  return Object.entries(counts)
    .map(([type, count]) => ({ type, count }))
    .sort((a, b) => b.count - a.count);
};

/**
 * Calculate appointment no-show rate.
 */
export const buildNoShowStats = (appointments) => {
  const past = appointments.filter((a) => new Date(a.dateTime) < new Date());
  const noShows = past.filter((a) => a.status === "No Show" || a.status === "Cancelled");
  return {
    total: past.length,
    noShows: noShows.length,
    rate: past.length > 0 ? Math.round((noShows.length / past.length) * 100) : 0,
  };
};

/**
 * Build guest capacity utilization stats from reservations.
 */
export const buildCapacityStats = (reservations) => {
  const MAX_CAPACITY = 200;
  const active = reservations.filter(
    (r) =>
      RESERVATION_ACTIVE_STATUSES.has(r.status) ||
      RESERVATION_COMPLETED_STATUSES.has(r.status)
  );
  if (active.length === 0) return { average: 0, utilization: 0, total: 0 };

  const totalSeats = active.reduce((s, r) => s + (r.numberOfSeats || 0), 0);
  const average = Math.round(totalSeats / active.length);
  const utilization = Math.round((average / MAX_CAPACITY) * 100);
  return { average, utilization, total: active.length };
};

/**
 * Build top add-ons popularity from reservations.
 */
export const buildAddOnPopularity = (reservations) => {
  const counts = {};
  reservations
    .filter((r) => !RESERVATION_INACTIVE_STATUSES.has(r.status))
    .forEach((r) => {
      const addOns = r.addOns || [];
      addOns.forEach((ao) => {
        const name = ao.addOn?.name || ao.name || "Unknown";
        counts[name] = (counts[name] || 0) + (ao.quantity || 1);
      });
    });
  return Object.entries(counts)
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 6);
};

/**
 * Build star rating distribution for the bar chart.
 */
export const buildRatingBars = (distribution) => {
  return [5, 4, 3, 2, 1].map((star) => ({
    star: `${star}★`,
    count: distribution[star] || 0,
  }));
};
